import { DepressionContentPage } from "@/components/DepressionContentPage";

const WhenAntidepressantsDontWork = () => (
  <DepressionContentPage
    seo={{
      title:
        "When Antidepressants Aren't Enough: Your Next Options | Neuro Wellness",
      description:
        "If antidepressants haven't provided enough relief, you're not out of options. Learn about treatment-resistant depression and interventional approaches like TMS, SAINT®, and SPRAVATO®.",
      canonical: "/when-antidepressants-dont-work",
      keywords:
        "antidepressants not working, treatment-resistant depression, next depression treatment, TMS after medication, SPRAVATO",
    }}
    badge="When Medication Isn't Enough"
    heroTitle="When Antidepressants Aren't Enough: Understanding Your Next Options"
    heroSubtitle="If you've tried medication and are still experiencing depression, you are not out of options. This page explains what may come next and why a different approach might be worth exploring."
    sections={[
      {
        heading: "Depression Treatment Can Take Trial and Adjustment",
        variant: "text",
        body: (
          <p>
            Finding the right treatment for depression often involves trial and
            adjustment. A medication that helps one person may not help another.
            Doses may need to change. Sometimes a medication works for a while
            and then seems to stop. This is common, and it doesn't mean
            treatment has failed you.
          </p>
        ),
      },
      {
        heading: "Why One Antidepressant May Work Differently From Another",
        variant: "text",
        body: (
          <p>
            Different antidepressants act on different chemical systems in the
            brain. What works for one person may not work for another, and side
            effects vary from person to person. This is why prescribers may try
            several medications, adjust doses, or combine treatments before
            considering other approaches.
          </p>
        ),
      },
      {
        heading: "What Treatment-Resistant Depression Means",
        variant: "text",
        body: (
          <p>
            Treatment-resistant depression (TRD) is a clinical term for
            depression that hasn't responded adequately to one or more
            antidepressant trials at adequate doses and duration. It isn't a
            judgment — it's a category that exists because this situation is
            common enough to need one, and because treatments were developed
            specifically for it.
          </p>
        ),
      },
      {
        heading: "When Doctors May Discuss Additional Treatment Options",
        variant: "text",
        body: (
          <p>
            When standard treatments haven't provided enough relief, a clinician
            may discuss interventional approaches — treatments that work through
            different mechanisms than oral medication. These are not
            replacements for medication; they may be used alongside it or
            considered when medication alone hasn't been sufficient.
          </p>
        ),
      },
      {
        heading: "TMS",
        variant: "text",
        body: (
          <p>
            Transcranial magnetic stimulation is a non-invasive, FDA-cleared
            treatment that uses magnetic pulses to stimulate areas of the brain
            associated with mood. It does not require anesthesia and is
            performed in an outpatient setting.
          </p>
        ),
      },
      {
        heading: "Accelerated TMS & SAINT®",
        variant: "text",
        body: (
          <p>
            Accelerated TMS protocols compress treatment into a shorter
            timeframe. SAINT® is an FDA-cleared accelerated protocol that uses
            functional MRI to individually map the treatment target. Whether
            either is appropriate is a clinical determination.
          </p>
        ),
      },
      {
        heading: "SPRAVATO®",
        variant: "text",
        body: (
          <p>
            SPRAVATO® (esketamine) is an FDA-approved prescription nasal spray
            for treatment-resistant depression, administered under supervision
            at a certified center. It works through a different mechanism than
            oral antidepressants.
          </p>
        ),
      },
      {
        heading: "What If You're Currently Starting a New Medication?",
        variant: "callout",
        callout:
          "Do not stop or change prescribed medication without discussing it with your prescribing healthcare provider. If you are starting a new antidepressant, it's often worth allowing adequate time to assess its effect before considering other options. Your prescriber can help you think through the timing.",
      },
    ]}
    faq={[
      {
        question:
          "How many antidepressants do I need to try before other options?",
        answer:
          "There is no fixed number. Clinical criteria for treatment-resistant depression often reference two or more adequate trials. However, the decision to explore other options is made with your clinician based on your full history.",
      },
      {
        question: "Can I try TMS while still taking antidepressants?",
        answer:
          "TMS may be used alongside oral antidepressants. Whether that's appropriate for you is a clinical decision made with your care team. Do not stop medication on your own.",
      },
      {
        question: "Does trying other options mean giving up on medication?",
        answer:
          "Not necessarily. Interventional treatments may be used alongside medication or considered when medication alone hasn't been sufficient. Your prescriber helps coordinate your overall plan.",
      },
    ]}
    relatedLinks={[
      {
        title: "Depression Treatment Options",
        href: "/depression-treatment-options",
      },
      {
        title: "Compare Depression Treatments",
        href: "/compare-treatments",
      },
      {
        title: "What Is Treatment-Resistant Depression?",
        href: "/what-is-treatment-resistant-depression",
      },
      { title: "Depression Treatment FAQ", href: "/depression-treatment-faq" },
    ]}
    finalCtaHeading="Explore Your Next Treatment Options"
    finalCtaText="If antidepressants haven't provided enough relief, there are other approaches worth understanding. Speak with our team about your history and what may be appropriate."
  />
);

export default WhenAntidepressantsDontWork;
